import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Button,
  Box,
  Container,
  Paper,
  Alert,
  Menu,
  Badge,
  Divider,
  TextField,
  Grid
} from '@mui/material';
import {
  Apps as AppsIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';

import MonthlyStatementOfOutpatients from './MouthlyStatementOfOutpatient';
import MonthlyStatementOfInpatients from './MonthlyStatementOfInpatients';
import MonthlyOpdMorbidityReturns from './MonthlyOpdMorbidityReturn';
import MonthlySurgicalOperationsReport from './MonthlySurgicalOperationsReport';
import MonthlyLabResults from './MonthlyLabResults';
import { DHimsperiods } from '../BaseURL';

// Sample page components for other datasets
const PatientRegistrationPage = ({ period }) => (
  <Box sx={{ p: 4, width: '100%' }}>
    <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
      Patient Registration
    </Typography>
    <Paper sx={{ p: 3, mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Patient ID" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Full Name" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Date of Birth" type="date" variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Gender" select variant="outlined" size="small">
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Address" variant="outlined" size="small" multiline rows={2} />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" sx={{ bgcolor: '#11998e', '&:hover': { bgcolor: '#0d7a6f' } }}>
            Save Patient
          </Button>
        </Grid>
      </Grid>
    </Paper>
  </Box>
);

const OutpatientServicesPage = ({ period }) => (
  <Box sx={{ p: 4, width: '100%' }}>
    <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
      Outpatient Services
    </Typography>
    <Paper sx={{ p: 3, mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Number of Consultations" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="New Cases" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Follow-up Cases" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Referrals" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" sx={{ bgcolor: '#11998e', '&:hover': { bgcolor: '#0d7a6f' } }}>
            Submit Data
          </Button>
        </Grid>
      </Grid>
    </Paper>
  </Box>
);

const InpatientServicesPage = ({ period }) => (
  <Box sx={{ p: 4, width: '100%' }}>
    <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
      Inpatient Services
    </Typography>
    <Paper sx={{ p: 3, mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Total Admissions" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Total Discharges" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Bed Occupancy Rate (%)" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Average Length of Stay (days)" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" sx={{ bgcolor: '#11998e', '&:hover': { bgcolor: '#0d7a6f' } }}>
            Submit Data
          </Button>
        </Grid>
      </Grid>
    </Paper>
  </Box>
);

const LaboratoryServicesPage = ({ period }) => (
  <Box sx={{ p: 4, width: '100%' }}>
    <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
      Laboratory Services
    </Typography>
    <Paper sx={{ p: 3, mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Blood Tests Conducted" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Urine Tests Conducted" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="X-Ray Tests" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Culture Tests" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" sx={{ bgcolor: '#11998e', '&:hover': { bgcolor: '#0d7a6f' } }}>
            Submit Data
          </Button>
        </Grid>
      </Grid>
    </Paper>
  </Box>
);

const PharmacyServicesPage = ({ period }) => (
  <Box sx={{ p: 4, width: '100%' }}>
    <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
      Pharmacy Services
    </Typography>
    <Paper sx={{ p: 3, mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Prescriptions Filled" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Medication Stock Items" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Out of Stock Items" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Revenue Generated" type="number" variant="outlined" size="small" />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" sx={{ bgcolor: '#11998e', '&:hover': { bgcolor: '#0d7a6f' } }}>
            Submit Data
          </Button>
        </Grid>
      </Grid>
    </Paper>
  </Box>
);

const EmptyStatePage = () => (
  <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          width: 80,
          height: 80,
          border: '2px solid #757575',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'white'
        }}
      >
        <DescriptionIcon sx={{ fontSize: 40, color: '#757575' }} />
      </Box>
    </Box>
    
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: '#333' }}>
      Get started with data entry
    </Typography>
    
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Choose a data set, organisation unit, and period from the top bar
      to start entering data
    </Typography>
    
    <Button 
      variant="text" 
      size="small"
      sx={{ textTransform: 'none', color: '#1976d2' }}
    >
      Learn more about data entry
    </Button>
  </Box>
);

export default function HealthInformation() {
  const [dataSet, setDataSet] = useState('');
  const [period, setPeriod] = useState('');
  const [periods, setPeriods] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  // Define available datasets/pages
  const dataSetPages = {
    'monthly-statement-outpatients': { 
      label: 'Monthly Statement of Outpatients', 
      component: MonthlyStatementOfOutpatients 
    },
    'monthly-statement-inpatients': { 
      label: 'Monthly Statement of Inpatients', 
      component: MonthlyStatementOfInpatients 
    },
    'monthly-opd-morbidity': {
  label: 'Monthly OPD Morbidity Returns',
  component: MonthlyOpdMorbidityReturns
},

'monthly-surgical-operations': {
  label: 'Monthly Surgical Operations Report',
  component: MonthlySurgicalOperationsReport
},
'monthly-lab-results': {
  label: 'Monthly Lab Results',
  component: MonthlyLabResults
},
    'patient-registration': { 
      label: 'Patient Registration', 
      component: PatientRegistrationPage 
    },
    'outpatient-services': { 
      label: 'Outpatient Services', 
      component: OutpatientServicesPage 
    },
    'inpatient-services': { 
      label: 'Inpatient Services', 
      component: InpatientServicesPage 
    },
    'laboratory-services': { 
      label: 'Laboratory Services', 
      component: LaboratoryServicesPage 
    },
    'pharmacy-services': { 
      label: 'Pharmacy Services', 
      component: PharmacyServicesPage 
    }
  };

  
  const handleDataSetChange = (e) => {
    setDataSet(e.target.value);
  };

  const handleClearSelections = () => {
    setDataSet('');
    setPeriod('');
  };

  // Get the current page component to render
  const CurrentPageComponent = dataSet && dataSetPages[dataSet] 
    ? dataSetPages[dataSet].component 
    : EmptyStatePage;

  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const res = await fetch(DHimsperiods);
        const data = await res.json();

        if (data.success) {
          setPeriods(data.periods);
        }
      } catch (error) {
        console.error('Error loading periods:', error);
      }
    };

    loadPeriods();
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ bgcolor: '#11998e' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 0, mr: 3, fontSize: '1rem' }}>
            Health Information / Report
          </Typography>
          
          <Box sx={{ flexGrow: 1 }} />
        </Toolbar>
      </AppBar>

      {/* Filter Bar */}
      <Paper elevation={1} sx={{ p: 2, mb: 0, borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: '70px' }}>
              Data set
            </Typography>
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <Select
                value={dataSet}
                onChange={handleDataSetChange}
                displayEmpty
                sx={{ bgcolor: 'white' }}
              >
                <MenuItem value="">
                  <em>Choose a data set</em>
                </MenuItem>
                {Object.entries(dataSetPages).map(([key, page]) => (
                  <MenuItem key={key} value={key}>
                    {page.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: '50px' }}>
              Period
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                displayEmpty
                sx={{ bgcolor: 'white' }}
              >
                <MenuItem value="">
                  <em>Select period</em>
                </MenuItem>
                {periods.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Button 
            variant="text" 
            size="small" 
            sx={{ ml: 'auto', textTransform: 'none' }}
            onClick={handleClearSelections}
          >
            Clear selections
          </Button>

          <Button 
            variant="outlined" 
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Options
          </Button>
        </Box>
      </Paper>

      {/* Main Content Area - Dynamically loads selected page */}
      <Container 
        maxWidth={false} 
        sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          alignItems: dataSet ? 'flex-start' : 'center', 
          justifyContent: 'center', 
          py: 4 
        }}
      >
        <CurrentPageComponent period={period} />
      </Container>

      {/* Warning Alert */}
      {period === '202412' && (
        <Box sx={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}>
          <Alert 
            severity="warning" 
            sx={{ 
              boxShadow: 3,
              '& .MuiAlert-icon': {
                color: '#000'
              },
              bgcolor: '#ffd54f',
              color: '#000'
            }}
            onClose={() => {}}
          >
            The Period (2024 December) is not open or is invalid.
          </Alert>
        </Box>
      )}
    </Box>
  );
}